import { Info } from 'lucide-react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '@/components/common/Button';
import PageHeader from '@/components/common/PageHeader';
import SummarySection from '@/components/common/SummarySection';
import type { ProgramListItemDataFragment } from '@/gql/graphql';
import useDateFormats from '@/utils/useDateFormats';
import type { AssessmentRunsFormData } from '../AssessmentRunsFormConfig';

export interface ReviewAssessmentRunsStepProps {
    program?: ProgramListItemDataFragment | null;
    onSubmit: () => void;
    onPrev: () => void;
}

const ReviewAssessmentRunsStep = ({ program, onSubmit, onPrev }: ReviewAssessmentRunsStepProps) => {
    const { t } = useTranslation('programManagementPage');
    const { formatDateFromTo } = useDateFormats();

    const { isSubmitting } = useFormState();
    const { getValues } = useFormContext<AssessmentRunsFormData>();

    const formData = getValues();

    return (
        <div className="flex flex-col gap-10">
            <div className="flex flex-col gap-5">
                <PageHeader title={t('assessmentRuns.title')} noControlsRowGap />
                <SummarySection
                    title={t('summary.title')}
                    items={[
                        { label: t('summary.program'), value: program?.name },
                        { label: t('summary.programDescription'), value: program?.description },
                        { label: t('summary.course'), value: program?.courseInstance?.name },
                        { label: t('summary.group'), value: formData?.groupName },
                    ]}
                    icon={Info}
                />
            </div>

            <div className="flex flex-col gap-5">
                <div className="text-lg leading-7 font-bold text-neutral-900">
                    {t('summary.assessmentRunsHeading', { count: formData.assessmentRuns.length })}
                </div>

                <div className="flex flex-col gap-5">
                    {formData.assessmentRuns.map((run, index) => (
                        <div key={index} className="flex flex-col gap-2 rounded-[10px] bg-neutral-50 p-5">
                            <div className="text-base leading-6 font-bold text-neutral-900">
                                {run.name || `Assessment Run ${index + 1}`}
                            </div>
                            <div className="text-base leading-6 font-medium text-neutral-500">
                                {formatDateFromTo(run.startDate, run.endDate)}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="inline-flex items-start justify-end gap-2.5 self-stretch">
                    <Button variant="secondary" onClick={onPrev} disabled={isSubmitting}>
                        {t('assessmentRuns.buttons.previous')}
                    </Button>
                    <Button variant="primary" type="submit" onClick={onSubmit} disabled={isSubmitting}>
                        {t('assessmentRuns.buttons.save')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ReviewAssessmentRunsStep;
